<template>
  <div class="dashboard-card" :class="{ 'portrait-mode': mode === 'portrait' }">
    <div class="card-header">
      {{ title }}
    </div>
    <div class="card-content">
      <!-- Table Header -->
      <div class="table-header" :style="{ backgroundColor: primaryColor }">
        <div
          v-for="header in headers"
          :key="header"
          class="header-cell"
          :style="{ color: headerTextColor }"
        >
          {{ header }}
        </div>
      </div>

      <!-- Table Body -->
      <div class="table-body" :class="{ 'stretch-rows': rows.length >= 6 }">
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="table-row"
          :class="{
            'top-three': index < 3,
            'bottom-three': rows.length > 6 && index >= rows.length - 3
          }"
        >
          <div
            v-for="(cell, cellIndex) in row"
            :key="cellIndex"
            class="table-cell"
            :class="{
              'rank-cell': cellIndex === 0 && index < 3,
              'name-cell': cellIndex === 1
            }"
            :style="{ color: index < 3 ? primaryColor : '#4a4a4a' }"
          >
            <template v-if="cellIndex === 0 && index < 3">
              <div class="rank-icon" :style="{ backgroundImage: `url(${require('@/assets/leaderboard/rank-bg.png')})` }">
                {{ cell }}
              </div>
            </template>
            <template v-else-if="cellIndex === 0">
              <div class="rank-number">{{ cell }}</div>
            </template>
            <template v-else>
              {{ cell }}
            </template>
          </div>
          <!-- 除了前三名都显示"继续努力" -->
          <div v-if="index >= 3" class="encouragement-text">
            <div class="encourage-line">继续</div>
            <div class="encourage-line">努力</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';

@Component
export default class DashboardCard extends Vue {
  @Prop({ default: '默认标题' }) title
  @Prop({ default: '#8a5524' }) primaryColor
  @Prop({ default: '#ffd8a9' }) headerTextColor
  @Prop({ default: 'landscape' }) mode // 'landscape' | 'portrait'
  @Prop({
    default: () => ['排名', '所属队伍', '达成率', '销售业绩']
  }) headers
  @Prop({
    default: () => []
  }) rows
}
</script>

<style lang="scss" scoped>
.dashboard-card {
  width: 100%; // 确保完全跟随父容器宽度
  height: 100%;
  background: white;
  border-radius: 1.5vh;
  display: flex;
  flex-direction: column;
  overflow: hidden; // 防止内容溢出
  box-sizing: border-box;

  .card-header {
    padding: 0.8vh 2vh 0.5vh;
    font-size: 2vh;
    font-weight: 600;
    color: #8a5524;
    text-align: center;
    flex-shrink: 0;
  }

  .card-content {
    flex: 1;
    padding: 0.5vh 2vh 2vh;
    display: flex;
    flex-direction: column;
    min-height: 0;

    .table-header {
      display: flex;
      border-radius: 0.8vh;
      flex-shrink: 0;
      margin-bottom: 1vh;

      .header-cell {
        flex: 1;
        padding: 1.2vh 0.8vh;
        text-align: center;
        font-size: 1.6vh;
        font-weight: 600;
      }
    }

    .table-body {
      flex: 1;
      display: flex;
      flex-direction: column;

      &.stretch-rows {
        justify-content: space-between;

        .table-row {
          flex: 1;
        }
      }

      .table-row {
        display: flex;
        border-bottom: 1px solid #4a4a4a;
        align-items: center;
        min-height: 4vh; // 默认最小高度
        position: relative; // 为定位的鼓励文字提供相对定位基准

        &:last-child {
          border-bottom: none;
        }

        .table-cell {
          flex: 1;
          text-align: center;
          font-size: 2.2vh; // 增大默认字体
          font-weight: 500;
          padding: 0 1vh; // 增大默认padding
          display: flex;
          align-items: center;
          justify-content: center;

          &.rank-cell {
            padding: 0;
          }

          &.name-cell {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-width: 0;
          }
        }

        .table-cell:first-child {
          padding: 0;
        }

        .encouragement-text {
          position: absolute;
          right: 1vh; // 定位在最后一个table-cell的右侧
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          pointer-events: none; // 防止影响行的点击事件

          .encourage-line {
            font-size: 1.2vh; // 较小的字体
            font-weight: 500;
            color: #8a5524; // 使用和table-header背景一样的颜色
            line-height: 1.2;
            text-align: center;
          }
        }

        .rank-icon {
          width: 3vh;
          height: 3vh;
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4vh;
          font-weight: 700;
          color: white;
          text-shadow: 0 0.1vh 0.2vh rgba(0, 0, 0, 0.5);
        }

        .rank-number {
          width: 3vh;
          height: 3vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.8vh;
          font-weight: 500;
        }
      }
    }
  }

  // 竖屏模式样式调整 - 更大字体和更好的可读性
  &.portrait-mode {
    .card-header {
      font-size: 2.2vh; // 适中的标题字体
      padding: 1vh 1.5vh 0.8vh; // 增大内边距
      font-weight: 700; // 加粗字体
    }

    .card-content {
      padding: 0.8vh 1.5vh 1.5vh; // 增大内边距

      .table-header {
        margin-bottom: 1vh; // 增大间距
        border-radius: 1vh;

        .header-cell {
          padding: 1.2vh 0.5vh; // 适中的padding
          font-size: 1.8vh; // 适中的字体
          font-weight: 700;

          // 优化列宽比例以适应竖屏
          &:first-child {
            flex: 0.8; // 排名列
          }

          &:nth-child(2) {
            flex: 2; // 姓名/队伍列
          }

          &:nth-child(3) {
            flex: 1.2; // 达成率列
          }

          &:nth-child(4) {
            flex: 1.8; // 销售业绩列
          }
        }
      }

      .table-body {
        .table-row {
          min-height: 4.5vh; // 适中的行高
          border-bottom: 2px solid #e8e8e8; // 增大边框线宽度

          &:last-child {
            border-bottom: none; // 确保最后一行不显示下边框
          }

          .table-cell {
            font-size: 1.6vh; // 适中的字体大小
            padding: 0 0.5vh; // 增大padding
            font-weight: 500;

            // 排名列
            &:first-child {
              flex: 0.8;
              min-width: 3vh;
            }

            // 姓名/队伍列 (第二列)
            &:nth-child(2) {
              flex: 2 !important;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              min-width: 0;
            }

            &.name-cell {
              flex: 2 !important;
            }

            // 达成率列 (第三列)
            &:nth-child(3) {
              flex: 1.2;
            }

            // 销售业绩列 (第四列)
            &:nth-child(4) {
              flex: 1.8;
            }
          }

          .encouragement-text {
            right: 0.5vh; // 调整位置
            background: rgba(255, 255, 255, 0.95);
            border-radius: 0.5vh;
            padding: 0.5vh 0.3vh;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

            .encourage-line {
              font-size: 1.4vh; // 增大鼓励文字字体
              line-height: 1.2;
              font-weight: 600;
            }
          }

          .rank-icon {
            width: 4vh; // 增大排名图标尺寸
            height: 4vh;
            font-size: 1.8vh; // 增大字体
            flex-shrink: 0;
            font-weight: 700;
          }

          .rank-number {
            width: 4vh; // 增大排名数字尺寸
            height: 4vh;
            font-size: 2vh; // 增大字体
            flex-shrink: 0;
            font-weight: 600;
          }
        }

        // 竖屏模式下的拉伸模式
        &.stretch-rows {
          .table-row {
            flex: 1;
            min-height: 5vh; // 保持较大的最小高度
          }
        }
      }
    }
  }
}
</style>
